import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import GlobalLogo from '@/components/feature/GlobalLogo';

export default function RegisterPage() {
  const { signUp } = useAuth();
  const navigate = useNavigate();

  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!displayName.trim() || !email.trim() || !password.trim()) {
      setError('Completá todos los campos.');
      return;
    }

    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden.');
      return;
    }

    setLoading(true);
    const { error: authError, requiresEmailConfirmation } = await signUp(
      email.trim(),
      password,
      displayName.trim(),
    );
    setLoading(false);

    if (authError) {
      setError(authError.message || 'Error al crear la cuenta.');
      return;
    }

    if (requiresEmailConfirmation) {
      setSuccess('Revisá tu email para confirmar la cuenta.');
      return;
    }

    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <GlobalLogo />
          </Link>
          <h1 className="text-2xl font-semibold text-foreground-950 mt-6">
            Crear cuenta
          </h1>
          <p className="text-sm text-foreground-600 mt-2">
            Unite a RadioFrecuenciaWeb
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-background-50 border border-background-200/70 rounded-lg p-6 space-y-5"
        >
          {error && (
            <div className="bg-accent-100 text-accent-900 text-sm px-4 py-3 rounded-md border border-accent-200">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-primary-100 text-primary-900 text-sm px-4 py-3 rounded-md border border-primary-200">
              {success}
            </div>
          )}

          <div>
            <label htmlFor="displayName" className="block text-sm font-medium text-foreground-700 mb-1.5">
              Nombre
            </label>
            <input
              id="displayName"
              type="text"
              name="displayName"
              autoComplete="name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition"
              placeholder="Tu nombre o apodo"
              disabled={loading}
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
              Email
            </label>
            <input
              id="email"
              type="email"
              name="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition"
              placeholder="tu@email.com"
              disabled={loading}
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-foreground-700 mb-1.5">
              Contraseña
            </label>
            <input
              id="password"
              type="password"
              name="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition"
              placeholder="Mínimo 6 caracteres"
              disabled={loading}
            />
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-foreground-700 mb-1.5">
              Confirmar contraseña
            </label>
            <input
              id="confirmPassword"
              type="password"
              name="confirmPassword"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition"
              placeholder="Repetí la contraseña"
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 px-4 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
          >
            {loading ? 'Creando cuenta...' : 'Crear cuenta'}
          </button>
        </form>

        <p className="text-center text-sm text-foreground-600 mt-6">
          ¿Ya tenés cuenta?{' '}
          <Link to="/login" className="text-primary-500 hover:text-primary-600 font-medium transition">
            Iniciá sesión
          </Link>
        </p>
      </div>
    </div>
  );
}